import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useSidebar } from '../../hooks/useSidebar';
import { PreferencesProvider, usePreferences } from '../../context/PreferencesContext';
import { WhatsNewModal, type WhatsNewMode } from '../common/WhatsNewModal';
import { PageSkeleton } from '../common/LoadingSpinner';
import { LATEST_VERSION, releases, releasesSince, type Release } from '../../whats-new/releases';
import { WhatsNewContext, type WhatsNewOrigin } from '../../whats-new/WhatsNewContext';
import { track } from '../../lib/track';

type Panel = { releases: Release[]; mode: WhatsNewMode };

function LayoutInner() {
  const { isCollapsed } = useSidebar();
  const { ready, get, set } = usePreferences();
  // The panel opens by itself after login on every release since the one last closed
  // (production receives several at once), and on purpose, on the whole history, from the
  // sidebar or a « NEW » tooltip. Same panel either way. After the automatic one closes, the
  // sidebar button glows so people know where to find it again.
  const [panel, setPanel] = useState<Panel | null>(null);
  const [glow, setGlow] = useState(false);

  useEffect(() => {
    if (!ready) return;
    const seen = get<string>('whats-new-seen');
    if (seen === LATEST_VERSION) return;
    const unseen = releasesSince(seen);
    if (unseen.length === 0) return;
    setPanel({ releases: unseen, mode: 'first' });
    track('whats-new', 'welcome', 'whats-new', {
      from_version: unseen[unseen.length - 1].version,
      to_version: unseen[0].version,
      count: unseen.length,
    });
  }, [ready, get]);

  const closeWhatsNew = useCallback(() => {
    setPanel((current) => {
      if (current?.mode === 'first') setGlow(true);
      return null;
    });
    set('whats-new-seen', LATEST_VERSION);
  }, [set]);

  const openWhatsNew = useCallback((from: WhatsNewOrigin = 'sidebar') => {
    setGlow(false);
    setPanel({ releases, mode: 'history' });
    track('whats-new', 'open', 'whats-new', { from });
  }, []);

  return (
    <WhatsNewContext.Provider value={{ openWhatsNew, panelOpen: panel !== null }}>
    <div className="flex h-full bg-neutral-25">
      {/* Sidebar with built-in mobile support */}
      <Sidebar onShowWhatsNew={openWhatsNew} whatsNewGlow={glow} onWhatsNewGlowEnd={() => setGlow(false)} />

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <main className="flex-1 relative overflow-y-auto focus:outline-none scrollbar-custom">
          <div className="py-8 lg:py-10">
            <div className={`max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 transition-all duration-300 ${
              isCollapsed ? 'lg:pl-10' : 'lg:pl-10'
            }`}>
              <div className="animate-fade-in">
                {/* Pages read their filters from sessionStorage at mount: wait for the hydration */}
                {ready ? <Outlet /> : <PageSkeleton />}
              </div>
            </div>
          </div>
        </main>
      </div>

      <WhatsNewModal
        open={panel !== null}
        onClose={closeWhatsNew}
        releases={panel?.releases}
        mode={panel?.mode}
      />
    </div>
    </WhatsNewContext.Provider>
  );
}

export function Layout() {
  return (
    <PreferencesProvider>
      <LayoutInner />
    </PreferencesProvider>
  );
}
